"use client";

import { formatMsgQuotes, getDetailJsonLd } from "@utils";
import { memo, useState } from "react";

import { FaqShare } from "@/src/features/api/ChatApi";
import { Icon } from "@ospweb/sdk-ui";
import { Markdown } from "../Markdown";
import { SourceType } from "../Chat/Source";
import { cn } from "utils";

interface FAQ {
  /** FAQ唯一标识符 */
  id: string;
  /** FAQ问题 */
  question: string;
  /** FAQ答案 */
  answer?: string;
}

interface FAQItemProps {
  /** FAQ问题 */
  question: string;
  /** FAQ答案 */
  answer?: string;
  /** 获取答案的方法，当没有传入answer时调用 */
  fetchAnswer?: () => Promise<void> | undefined;
  /** 容器自定义类名 */
  className?: string;
  sources?: SourceType[];
  index: number;
  handleToggle: (index: number) => void;
  isExpanded: boolean;
}

/**
 * FAQ 项组件
 */
const FAQItem = memo((props: FAQItemProps) => {
  const {
    question,
    answer,
    className,
    sources,
    index,
    handleToggle,
    isExpanded,
  } = props;

  return (
    <div className={cn(className)}>
      <div
        data-openreplay-label="TokenDetails · SymbolFAQ · div"
        className={cn(
          "flex justify-between items-center cursor-pointer transition-all duration-300 ease-in-out group",
          { "h-auto": isExpanded },
        )}
        onClick={() => handleToggle(index)}
      >
        <h3
          aria-label="Symbol FAQ h3"
          data-openreplay-label="Symbol FAQ h3 Semantic"
          id="sem-symbol-faq-h3"
          className="body_medium_l text-content_primary duration-200 transition-all md:group-hover:text-content_secondary m-0"
        >
          {question}
        </h3>
        <Icon
          name="close"
          size={20}
          className={cn(
            "transition-transform duration-300 ease-in-out text-content_brand_primary duration-200 transition-all md:group-hover:text-content_brand_secondary",
            isExpanded ? "rotate-0" : "-rotate-45",
          )}
        />
      </div>
      <div
        className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          isExpanded ? "opacity-100" : "max-h-0 opacity-0",
        )}
      >
        <div className="body_m text-black md:text-white/50 pr-15 overflow-y-auto no-scrollbar relative">
          <Markdown
            source={sources}
            showSelect={false}
            className="[&_*]:!text-[14px] max-md:[&_*]:!text-[12px] [&_td]:!min-w-[66px] [&_th]:!min-w-[66px] [&_p]:!text-content_tertiary [&_p]:!leading-[20px]"
          >
            {answer || ""}
          </Markdown>
        </div>
      </div>
    </div>
  );
});

FAQItem.displayName = "FAQItem";

interface SymbolFAQProps {
  /** FAQ标题 */
  title?: string;
  /** 获取答案的方法，当没有传入answer时调用 */
  fetchAnswer?: (question: string, id: string) => Promise<void>;
  /** 容器自定义类名 */
  className?: string;
  /** 代币symbol */
  answer?: string;
  faqs?: MaybeNullable<object & FaqShare[]>;
  name: string;
}

export enum SymbolFAQType {
  TOKEN = "token",
  STOCK = "stock",
}

/**
 * 代币FAQ组件
 */
const SymbolFAQ = memo(
  ({ title = "FAQ", className, faqs, name = "" }: SymbolFAQProps) => {
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const handleToggle = (index: number) => {
      setExpandedIndex(expandedIndex === index ? null : index);
    };
    // 如果没有FAQ数据，则不渲染
    if (!faqs || faqs.length === 0) {
      return <div className="md:pb-30" />;
    }

    const faqJsonLd = getDetailJsonLd({
      name,
      faq: faqs,
    });
    return (
      <div className="text-content_primary body_medium_l w-full mb-6 px-4 md:px-0">
        <div className={cn("w-full flex flex-col gap-4", className)}>
          <h2
            aria-label="Symbol FAQ h2"
            data-openreplay-label="Symbol FAQ h2 Semantic"
            id="sem-symbol-faq-h2"
            className="headline_regular_s text-content_primary mb-3 md:mb-1"
          >
            {title}
          </h2>
          <div className="w-full divide-y space-y-6 md:space-y-4 divide-border_white_transparent10 ">
            {faqs?.map((faq, index) => {
              const answerMessage = faq?.messages?.find(
                (item: any) => item.role === "assistant",
              );
              const { content, sources } = answerMessage || {};
              return (
                <FAQItem
                  key={faq?.share_id || index}
                  question={faq?.question || ""}
                  answer={faq?.answer || formatMsgQuotes(content || "")}
                  sources={sources || []}
                  index={index}
                  handleToggle={handleToggle}
                  isExpanded={index === expandedIndex}
                  className={cn({ "pt-6 md:pt-4": index !== 0 })}
                />
              );
            })}
          </div>
        </div>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqJsonLd).replace(/</g, "\\u003c"),
          }}
        />
      </div>
    );
  },
);

SymbolFAQ.displayName = "SymbolFAQ";

export { SymbolFAQ, FAQItem };
export type { SymbolFAQProps, FAQItemProps, FAQ };
